import * as z from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { Button } from "@/components/ui/button";
import type { Decree } from "@/types/decree.type";
import { DecreeCategory } from "@/types/gql/graphql";
import { DropdownSelect } from "@/components/form-control";
import { decreeCategoryType } from "./decree.const";
import { useForm } from "react-hook-form";
import { useUpdateDecreeCategory } from "@/modules/restriction/hooks/use-query-decree-file";
import { zodResolver } from "@hookform/resolvers/zod";

const decreeSchema = z.object({
  category: z.string(),
});

type DecreeFormValues = z.infer<typeof decreeSchema>;

type EditDecreeProps = {
  decreeData: Decree;
  closeModal: () => void;
  refetchDecree: () => void;
};

export const ChangeDecreeCategoryModal = ({ decreeData, closeModal, refetchDecree }: EditDecreeProps) => {
  const { updateDecreeCategory } = useUpdateDecreeCategory(refetchDecree);
  const form = useForm<DecreeFormValues>({
    resolver: zodResolver(decreeSchema),
    mode: "all",
    defaultValues: {
      category: decreeData?.category ?? DecreeCategory.Unknown,
    },
  });

  const saveDecree = async (data: DecreeFormValues) => {
    const variables = {
      id: decreeData?.id,
      input: {
        newCategory: data.category as DecreeCategory,
      },
    };
    await updateDecreeCategory({ variables });
    closeModal();
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit decree</DialogTitle>
          <DialogDescription>Decree information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(saveDecree)} className="space-y-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <DropdownSelect value={value} options={decreeCategoryType} onValueChange={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={closeModal} variant="secondary" type="button">
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
